import type {
  ChiTietGiayBaoNo,
  GiayBaoNo,
  GiayBaoNoInput,
  GiayBaoNoInvoiceLookup,
  GiayBaoNoAmount,
  PhiNganHangGiayBaoNo,
  ThueGiayBaoNo
} from '@/types/schemas';
import type { GiayBaoNoSearchFieldState } from '../hooks/useSearchFieldStates';
import { resolvePaymentDocumentInvoiceId } from '@/lib/payment-document-utils';
import { transformDocumentNumber } from '@/components/custom/erp';
import { isValidUUID } from '@/lib/uuid-validator';
import type { calculateTotals } from './calc-util';
import { FormMode } from '@/types/form';

interface EntityUnitRef {
  uuid?: string | null;
}

type GiayBaoNoTotals = ReturnType<typeof calculateTotals>;
type GiayBaoNoFormValues = Partial<GiayBaoNoInput> & {
  dien_giai?: string | null;
  so_ct0?: string | null;
  ma_nt?: string | null;
  ty_gia?: GiayBaoNoAmount | null;
};

interface HoaDonSourceLine extends Partial<ChiTietGiayBaoNo>, Record<string, unknown> {
  tk_dt_data?: ChiTietGiayBaoNo['tk_no_data'] | null;
  ma_dv_data?: Partial<ChiTietGiayBaoNo> & {
    thue_suat_data?: ChiTietGiayBaoNo['ma_thue_data'] | null;
  };
}

export interface HoaDonSourceData extends Record<string, unknown> {
  uuid?: string;
  ngay_ct?: string | null;
  so_ct?: string | null;
  dien_giai?: string | null;
  ma_kh?: string | null;
  ma_kh_data?: GiayBaoNo['ma_kh_data'] | null;
  ong_ba?: string | null;
  dia_chi?: string | null;
  ma_nt?: string | null;
  ty_gia?: GiayBaoNoAmount;
  t_thue_nt?: GiayBaoNoAmount;
  t_thue?: GiayBaoNoAmount;
  t_cp_nt?: GiayBaoNoAmount;
  t_cp?: GiayBaoNoAmount;
  chi_tiet_hoa_don?: HoaDonSourceLine[];
  chi_tiet_data?: HoaDonSourceLine[];
  tk_data?: ChiTietGiayBaoNo['tk_no_data'] | null;
  ma_nt_data?: { ma_nt?: string | null } | null;
  t_tt?: GiayBaoNoAmount;
}

export const transformDetailRows = (
  detailRows: ChiTietGiayBaoNo[],
  formMode: FormMode,
  dien_giai?: string | null,
  maNgv?: string
) => {
  return detailRows?.map((row, index) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && formMode !== 'add' && { uuid: row.uuid }),
    dien_giai: row.dien_giai || dien_giai || null,
    ma_kh: row.ma_kh_data?.uuid || null,
    id_hd: resolvePaymentDocumentInvoiceId(maNgv, row.id_hd_data?.ID, row.id_hd),
    tk_no: row.tk_no_data?.uuid || null,
    tien_nt: row.tien_nt || row.id_hd_data?.tien_tren_hd || null,
    line_dn: row.line_dn || null,
    ma_bp: row.ma_bp_data?.uuid || null,
    ma_vv: row.ma_vv_data?.uuid || null,
    ma_hd: row.ma_hd_data?.uuid || null,
    ma_dtt: row.ma_dtt_data?.uuid || null,
    ma_ku: row.ma_ku_data?.uuid || null,
    ma_phi: row.ma_phi_data?.uuid || null,
    ma_sp: row.ma_sp_data?.uuid || null,
    ma_lsx: row.ma_lsx || null,
    ma_cp0: row.ma_cp0_data?.uuid || null
  }));
};

const transformThueRows = (thueRows: ThueGiayBaoNo[], formMode: FormMode) => {
  return thueRows?.map((row, index) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && formMode !== 'add' && { uuid: row.uuid }),
    so_ct0: row.so_ct0 || '',
    so_ct2: row.so_ct2 || '',
    ngay_ct0: row.ngay_ct0 || '',

    ma_thue: row.ma_thue_data?.uuid || '',
    thue_suat: row.ma_thue_data?.thue_suat || 0,
    ma_mau_ct: row.ma_mau_ct_data?.uuid || '',
    ma_mau_bc: row.mau_bao_cao || '1',
    ma_tc_thue: row.ma_tc_thue_data?.uuid || '',

    ma_kh: row.ma_kh_data?.uuid || '',
    ten_kh_thue: row.ma_kh_data?.customer_name || '',
    dia_chi: row.ma_kh_data?.address || row.dia_chi || '',
    ma_so_thue: row.ma_kh_data?.tax_code || row.ma_so_thue || '',

    ten_vt_thue: row.ten_vt_thue || '',

    t_tien_nt: row.t_tien_nt || 0.0,
    t_thue_nt: row.t_thue_nt || 0.0,

    tk_thue_no: row.tk_thue_no_data?.uuid || '',
    tk_du: row.tk_du_data?.uuid || '',

    ma_kh9: row.ma_kh9_data?.uuid || '',

    ma_tt: row.ma_tt_data?.uuid || '',

    ghi_chu: row.ghi_chu || '',

    ma_bp: row.ma_bp_data?.uuid || null,
    ma_vv: row.ma_vv_data?.uuid || null,
    ma_hd: row.ma_hd_data?.uuid || null,
    ma_dtt: row.ma_dtt_data?.uuid || null,
    ma_ku: row.ma_ku_data?.uuid || null,
    ma_phi: row.ma_phi_data?.uuid || null,
    ma_sp: row.ma_sp_data?.uuid || null,
    ma_lsx: row.ma_lsx_data?.uuid || null,
    ma_cp0: row.ma_cp0_data?.uuid || null
  }));
};

const transformPhiNganHangRows = (phiNganHangRows: PhiNganHangGiayBaoNo[], formMode: FormMode) => {
  return phiNganHangRows?.map((row, index) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && formMode !== 'add' && { uuid: row.uuid }),
    ma_kh: row.ma_kh_data?.uuid || '',

    ma_cpnh: row.ma_cpnh_data?.uuid || '',
    tien_cp_nt: row.tien_cp_nt || 0.0,

    so_ct0: row.so_ct0 || '',
    so_ct2: row.so_ct2 || '',
    ngay_ct0: row.ngay_ct0 || new Date().toISOString().split('T')[0],

    dien_giai: row.dien_giai || '',

    tk_cpnh: row.tk_cpnh_data?.uuid || row.ma_cpnh_data?.tk_cpnh_data?.uuid || '',
    tk_du: row.tk_du_data?.uuid || '',
    tk_thue: row.ma_cpnh_data?.tk_thue_data?.uuid || '',

    ma_thue: row.ma_thue_data?.uuid || '',
    thue_suat: row.ma_thue_data?.thue_suat || 0,
    t_thue_nt: row.t_thue_nt || 0.0,

    ma_bp: row.ma_bp_data?.uuid || null,
    ma_vv: row.ma_vv_data?.uuid || null,
    ma_hd: row.ma_hd_data?.uuid || null,
    ma_dtt: row.ma_dtt_data?.uuid || null,
    ma_ku: row.ma_ku_data?.uuid || null,
    ma_phi: row.ma_phi_data?.uuid || null,
    ma_sp: row.ma_sp_data?.uuid || null,
    ma_lsx: row.ma_lsx_data?.uuid || null,
    ma_cp0: row.ma_cp0_data?.uuid || null
  }));
};

export const transformFormData = (
  data: GiayBaoNoFormValues,
  state: GiayBaoNoSearchFieldState,
  totals: GiayBaoNoTotals,
  entityUnit: EntityUnitRef | null | undefined,
  detailsRow: ChiTietGiayBaoNo[],
  taxRows: ThueGiayBaoNo[],
  feeRows: PhiNganHangGiayBaoNo[],
  formMode: FormMode
): GiayBaoNoInput => {
  const detail = transformDetailRows(detailsRow, formMode, data.dien_giai, state.loaiChungTu);
  const tax = transformThueRows(taxRows, formMode);
  const fee = transformPhiNganHangRows(feeRows, formMode);

  return {
    ...data,

    unit_id: entityUnit?.uuid,
    ma_ngv: state.loaiChungTu || '1',
    tknh: state.bank?.uuid || '',
    tk: state.account?.uuid || '',
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu),
    loai_hd: state.loai_hd || '1',
    ma_kh0: state.donViNhanTien?.uuid || '',
    ma_ngan_hang: state.nganHangNhanTien?.uuid || '',
    ma_tt: state.hanTT?.uuid || '',
    so_ct0: data.so_ct0,
    ma_kh: detail[0]?.ma_kh,

    t_tien_nt: totals.tong_tien,
    t_thue_nt: totals.tong_thue,
    t_thue: totals.tong_thue,
    t_cp_nt: totals.tong_phi,
    t_cp: totals.tong_phi,
    t_tt_nt: totals.tong_thanh_toan,
    t_tt: totals.tong_thanh_toan,
    ma_nt: data.ma_nt || '',
    ty_gia: data.ty_gia || 1,
    chi_tiet: detail,
    thue: tax,
    phi_ngan_hang: fee
  };
};

/**
 * Transform purchase invoice data to giay bao no initial form data
 * @param hoaDonData - Purchase invoice data from API
 * @returns Initial form data for giay bao no
 */
export const transformDataFromHoaDon = (hoaDonData: HoaDonSourceData) => {
  if (!hoaDonData) return {};

  const currentDate = new Date().toISOString().split('T')[0];

  const baseTime = Date.now();
  const chi_tiet_data = (hoaDonData.chi_tiet_hoa_don || []).map((item, index) => ({
    uuid: `temp-${baseTime}-${index}`,
    line: index + 1,
    dien_giai: hoaDonData.dien_giai,

    ma_kh_data: hoaDonData.ma_kh_data || null,

    tk_no_data: item.tk_dt_data || hoaDonData.tk_data || null,

    tien_nt: hoaDonData.t_tt || item.tien_nt || 0,
    ty_gia2: hoaDonData.ty_gia || 1,

    id_hd_data: {
      ID: hoaDonData.uuid,
      so_ct: hoaDonData.so_ct,
      ngay_ct: hoaDonData.ngay_ct,
      tk_data: hoaDonData.tk_data,
      ngoai_te: hoaDonData.ma_nt_data?.ma_nt,
      ty_gia: hoaDonData.ty_gia || 1,
      tien_tren_hd: hoaDonData.t_tt,
      tien_hd_nt: hoaDonData.t_tt,
      tien_con_phai_tt: hoaDonData.t_tt
    } satisfies GiayBaoNoInvoiceLookup,

    ma_thue_data: item.ma_thue_data || null,
    thue_suat: item.ma_thue_data?.thue_suat || 0,
    t_thue_nt: 0,

    ma_bp_data: item.ma_bp_data || null,
    ma_vv_data: item.ma_vv_data || null,
    ma_hd_data: item.ma_hd_data || null,
    ma_dtt_data: item.ma_dtt_data || null,
    ma_ku_data: item.ma_ku_data || null,
    ma_phi_data: item.ma_phi_data || null,
    ma_cp0_data: item.ma_cp0_data || null
  }));

  return {
    ma_ngv: '1',
    ngay_lct: currentDate,
    ngay_ct: currentDate,
    ngay_ct0: hoaDonData.ngay_ct,
    so_ct0: hoaDonData.so_ct || '',
    dien_giai: hoaDonData.dien_giai || '',

    ma_kh: hoaDonData.ma_kh_data?.uuid || null,
    ong_ba: hoaDonData.ma_kh_data?.contact_person || hoaDonData.ong_ba || '',
    dia_chi: hoaDonData.ma_kh_data?.address || hoaDonData.dia_chi || '',

    ma_nt: hoaDonData.ma_nt || '',
    ty_gia: hoaDonData.ty_gia || 1,

    status: '0',

    chi_tiet_data
  };
};

/**
 * Transform service invoice data to giay bao no initial form data
 * @param hoaDonData - Service invoice data from API
 * @returns Initial form data for giay bao no
 */
export const transformDataFromHoaDonDichVu = (hoaDonData: HoaDonSourceData) => {
  if (!hoaDonData) return {};

  const currentDate = new Date().toISOString().split('T')[0];

  const baseTime = Date.now();
  const chi_tiet_data = (hoaDonData.chi_tiet_data || []).map((item, index) => ({
    uuid: `temp-${baseTime}-${index}`,
    line: index + 1,
    dien_giai: hoaDonData.dien_giai,

    ma_kh_data: hoaDonData.ma_kh_data || null,

    tk_no_data: item.tk_dt_data || hoaDonData.tk_data || null,

    tien_nt: hoaDonData.t_tt || item.tien_nt || 0,
    ty_gia2: hoaDonData.ty_gia || 1,

    id_hd_data: {
      ID: hoaDonData.uuid,
      so_ct: hoaDonData.so_ct,
      ngay_ct: hoaDonData.ngay_ct,
      tk_data: hoaDonData.tk_data,
      ngoai_te: hoaDonData.ma_nt_data?.ma_nt,
      ty_gia: hoaDonData.ty_gia || 1,
      tien_tren_hd: hoaDonData.t_tt,
      tien_hd_nt: hoaDonData.t_tt,
      tien_con_phai_tt: hoaDonData.t_tt
    } satisfies GiayBaoNoInvoiceLookup,

    ma_thue_data: item.ma_thue_data || item.ma_dv_data?.thue_suat_data || null,
    thue_suat: item.ma_thue_data?.thue_suat || item.ma_dv_data?.thue_suat_data?.thue_suat || 0,
    t_thue_nt: 0,

    ma_bp_data: item.ma_bp_data || item.ma_dv_data?.ma_bp_data || null,
    ma_vv_data: item.ma_vv_data || item.ma_dv_data?.ma_vv_data || null,
    ma_hd_data: item.ma_hd_data || item.ma_dv_data?.ma_hd_data || null,
    ma_dtt_data: item.ma_dtt_data || item.ma_dv_data?.ma_dtt_data || null,
    ma_ku_data: item.ma_ku_data || item.ma_dv_data?.ma_ku_data || null,
    ma_phi_data: item.ma_phi_data || null,
    ma_cp0_data: item.ma_cp0_data || null
  }));

  return {
    ma_ngv: '1',
    ngay_lct: currentDate,
    ngay_ct: currentDate,
    ngay_ct0: hoaDonData.ngay_ct,
    so_ct0: hoaDonData.so_ct || '',
    dien_giai: hoaDonData.dien_giai || '',

    ma_kh: hoaDonData.ma_kh_data?.uuid || null,
    ong_ba: hoaDonData.ma_kh_data?.contact_person || hoaDonData.ong_ba || '',
    dia_chi: hoaDonData.ma_kh_data?.address || hoaDonData.dia_chi || '',

    ma_nt: hoaDonData.ma_nt || 'VND',
    ty_gia: hoaDonData.ty_gia || 1,

    status: '0',

    chi_tiet_data
  };
};
